import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Receipt, RefreshCw, AlertCircle, CheckCircle2,
  XCircle, ChevronDown, ChevronUp, Building2, Loader2,
  FileText, Banknote, ArrowRightLeft, Hash, Play, UserX,
  Search, History, ChevronRight, Calendar, Clock,
  Inbox, Plus, Trash2, Pencil, FileWarning, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (n) =>
  typeof n === 'number'
    ? `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; }
};

// ── Select con buscador para elegir un cliente de YiQi (filtra por nombre) ────
const BuscadorCliente = ({ apiUrl, token, value, nombre, vendedor, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const boxRef = useRef(null);

  // Búsqueda con debounce. El texto se envía en minúsculas (lowercase).
  useEffect(() => {
    if (!open) return;
    const texto = q.trim().toLowerCase();
    if (texto.length < 2) { setResultados([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const res = await fetch(
          `${apiUrl}/api/admin/recibos-cobro/clientes?q=${encodeURIComponent(texto)}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: ctrl.signal }
        );
        const data = await res.json();
        if (res.ok) setResultados(data.clientes || []);
      } catch (e) {
        if (e.name !== 'AbortError') setResultados([]);
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open, apiUrl, token]);

  // Cerrar el desplegable al hacer clic fuera.
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const elegir = (c) => {
    onSelect(c);
    setOpen(false);
    setQ('');
    setResultados([]);
  };

  return (
    <div className="relative" ref={boxRef}>
      {/* Control tipo "select" */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs transition-colors disabled:opacity-50 ${
          value
            ? 'bg-gray-900 border border-gray-600'
            : 'bg-gray-900 border border-amber-500/40'
        } ${open ? 'border-[#00a1df]' : ''}`}
      >
        {value ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500/80 shrink-0" />
        ) : (
          <Search className="w-3.5 h-3.5 text-amber-400 shrink-0" />
        )}
        <span className="min-w-0 flex-1">
          {value ? (
            <>
              <span className="block text-white truncate" title={nombre}>{nombre || `ID ${value}`}</span>
              {vendedor && <span className="block text-[10px] text-gray-500 truncate">Vendedor: {vendedor}</span>}
            </>
          ) : (
            <span className="text-gray-400">Seleccionar cliente…</span>
          )}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Panel: buscador + lista de opciones */}
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-gray-900 border border-gray-700 rounded-md shadow-2xl overflow-hidden">
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-700 bg-gray-900">
            <Search className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Escribí para filtrar por nombre…"
              className="w-full bg-transparent text-xs text-white placeholder-gray-500 focus:outline-none"
            />
            {buscando && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500 shrink-0" />}
          </div>
          <div className="max-h-56 overflow-y-auto">
            {q.trim().length < 2 ? (
              <div className="px-3 py-2 text-[11px] text-gray-500">Escribí al menos 2 letras para buscar.</div>
            ) : resultados.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-gray-500">{buscando ? 'Buscando…' : 'Sin resultados'}</div>
            ) : (
              resultados.map((c) => (
                <button
                  key={c.clie_id_clie}
                  type="button"
                  onClick={() => elegir(c)}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-800 transition-colors border-b border-gray-800/60 last:border-0 ${
                    c.clie_id_clie === value ? 'bg-gray-800/60' : ''
                  }`}
                >
                  <span className="block text-xs text-white truncate">{c.razon_social || '(sin nombre)'}</span>
                  <span className="block text-[10px] text-gray-500 font-mono">
                    ID {c.clie_id_clie}{c.cuit ? ` · CUIT ${c.cuit}` : ''}
                  </span>
                  {c.vendedor && (
                    <span className="block text-[10px] text-[#00a1df]/80 truncate">Vendedor: {c.vendedor}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Historial de procesos (log estilo Tareas Programadas) ────────────────────
const RUN_STATUS = {
  completo:  { label: 'Completo',  cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  parcial:   { label: 'Parcial',   cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  pendiente: { label: 'Pendiente', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  error:     { label: 'Error',     cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  vacio:     { label: 'Sin movimientos', cls: 'bg-gray-700 text-gray-400 border-gray-600' },
};

const RunStatusBadge = ({ status }) => {
  const s = RUN_STATUS[status] || { label: status || '—', cls: 'bg-gray-700 text-gray-400 border-gray-600' };
  return <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
};

const GRUPO_ESTADO = {
  creado:      { label: 'Recibo creado', cls: 'text-green-400', Icon: CheckCircle2 },
  duplicado:   { label: 'Ya existía',    cls: 'text-amber-400', Icon: AlertCircle },
  pendiente:   { label: 'Pendiente',     cls: 'text-gray-400',  Icon: Clock },
  sin_cliente: { label: 'Sin cliente',   cls: 'text-amber-400', Icon: UserX },
  error:       { label: 'Error',         cls: 'text-red-400',   Icon: XCircle },
};

// Detalle de un cliente dentro de una corrida (expandible a sus movimientos).
// En estados editables (pendiente / sin_cliente) permite asignar el cliente.
const GrupoLog = ({ grupo, modo, apiUrl, token, asignacion, onAsignar }) => {
  const [open, setOpen] = useState(false);
  const meta = GRUPO_ESTADO[grupo.estado_recibo] || GRUPO_ESTADO.pendiente;
  const Icon = meta.Icon;
  const esCheques = modo === 'cheques';
  const items = grupo.items || [];
  const candidatos = grupo.candidatos || [];
  const editable = grupo.estado_recibo === 'pendiente' || grupo.estado_recibo === 'sin_cliente';
  const noEncontrado = grupo.estado_cliente === 'no_encontrado';
  const multi = candidatos.length > 1;

  const clienteId = asignacion?.clie_id_clie || grupo.clie_id_clie || null;
  const clienteNombre = asignacion?.cliente_nombre || grupo.cliente_nombre
    || (clienteId ? (candidatos.find((c) => c.clie_id_clie === clienteId)?.razon_social) : null)
    || null;

  return (
    <div className="border border-gray-800 rounded-lg">
      <div className="w-full flex items-center gap-3 px-3 py-2.5 bg-gray-900/40 rounded-t-lg">
        <button onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-90 transition-opacity text-left">
          {open ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-white truncate">{grupo.razon_social_pdf || '(sin nombre)'}</p>
            <p className="text-[10px] text-gray-500 font-mono">
              CUIT {grupo.cuit} · {grupo.cantidad_items} mov. · {fmtMoney(grupo.total)}
              {clienteNombre ? ` · YiQi: ${clienteNombre}` : ''}
              {clienteId ? ` (ID ${clienteId})` : ''}
            </p>
          </div>
        </button>

        {/* Estado / asignación de cliente */}
        <div className="shrink-0 w-60 flex flex-col items-end gap-1">
          {!editable ? (
            <span className={`flex items-center gap-1 text-[11px] font-bold ${meta.cls}`}>
              <Icon className="w-3.5 h-3.5" />
              {meta.label}{grupo.recibo_id ? ` #${grupo.recibo_id}` : ''}
            </span>
          ) : (
            <div className="w-full">
              <span className="flex items-center justify-end gap-1 text-[10px] font-bold text-gray-400 mb-1">
                <Clock className="w-3 h-3" /> Pendiente — asigná cliente
              </span>
              {noEncontrado ? (
                <BuscadorCliente
                  apiUrl={apiUrl}
                  token={token}
                  value={asignacion?.clie_id_clie}
                  nombre={asignacion?.cliente_nombre}
                  vendedor={asignacion?.vendedor}
                  onSelect={(c) => onAsignar(grupo.cuit, {
                    clie_id_clie: c.clie_id_clie, cliente_nombre: c.razon_social, vendedor: c.vendedor || '',
                  })}
                />
              ) : multi ? (
                <select
                  value={clienteId || ''}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    const c = candidatos.find((x) => x.clie_id_clie === id);
                    onAsignar(grupo.cuit, { clie_id_clie: id, cliente_nombre: c?.razon_social || null });
                  }}
                  className="w-full bg-gray-900 border border-gray-600 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#00a1df] focus:outline-none"
                >
                  <option value="">— Elegir cliente —</option>
                  {candidatos.map((c) => (
                    <option key={c.clie_id_clie} value={c.clie_id_clie}>
                      {c.razon_social} (ID {c.clie_id_clie})
                    </option>
                  ))}
                </select>
              ) : (
                <span className="block text-right text-[11px] text-gray-300 truncate" title={clienteNombre}>
                  {clienteNombre || '—'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {grupo.error && (
        <p className="px-3 py-1.5 text-[11px] text-red-400/80 bg-red-500/5 border-t border-gray-800">{grupo.error}</p>
      )}
      {open && (
        <div className="px-3 py-2 bg-black/30 border-t border-gray-800 overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-gray-500 uppercase tracking-wider border-b border-gray-800">
                {esCheques ? (
                  <>
                    <th className="text-left py-1.5 pr-3 font-bold">Nº Cheque</th>
                    <th className="text-left py-1.5 pr-3 font-bold">Banco emisor</th>
                    <th className="text-left py-1.5 pr-3 font-bold">Fecha pago</th>
                    <th className="text-right py-1.5 font-bold">Importe</th>
                  </>
                ) : (
                  <>
                    <th className="text-left py-1.5 pr-3 font-bold">Tipo</th>
                    <th className="text-left py-1.5 pr-3 font-bold">Referencia</th>
                    <th className="text-left py-1.5 pr-3 font-bold">Fecha</th>
                    <th className="text-right py-1.5 font-bold">Importe</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/70">
              {items.map((it, idx) => (
                <tr key={idx}>
                  {esCheques ? (
                    <>
                      <td className="py-1.5 pr-3 font-mono text-cyan-400">{it.numero || '—'}</td>
                      <td className="py-1.5 pr-3 text-gray-400 max-w-xs truncate" title={it.banco_emisor}>{it.banco_emisor || '—'}</td>
                      <td className="py-1.5 pr-3 text-gray-500">{it.fecha_pago || '—'}</td>
                      <td className="py-1.5 text-right font-bold text-white">{fmtMoney(it.importe)}</td>
                    </>
                  ) : (
                    <>
                      <td className="py-1.5 pr-3 text-gray-400">{it.tipo || '—'}</td>
                      <td className="py-1.5 pr-3 text-gray-500 max-w-xs truncate" title={it.referencia}>{it.referencia || '—'}</td>
                      <td className="py-1.5 pr-3 text-gray-500">{it.fecha || '—'}</td>
                      <td className="py-1.5 text-right font-bold text-white">{fmtMoney(it.importe)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Nombre del archivo + visor del PDF original del proceso.
const PdfViewer = ({ apiUrl, token, runId, archivo, disponible }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || url || !disponible) return;
    let creado = null;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${apiUrl}/api/admin/recibos-cobro/runs/${runId}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || 'No se pudo cargar el PDF');
        }
        const blob = await res.blob();
        creado = URL.createObjectURL(blob);
        setUrl(creado);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
    return () => { if (creado) URL.revokeObjectURL(creado); };
  }, [open, url, disponible, apiUrl, token, runId]);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  return (
    <div className="border border-gray-700 rounded-lg bg-gray-900/40">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-[#00a1df] shrink-0" />
          <span className="text-sm font-bold text-white truncate" title={archivo}>{archivo || 'Documento PDF'}</span>
        </div>
        {disponible ? (
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-200 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-all whitespace-nowrap"
          >
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
            {open ? 'Ocultar PDF' : 'Ver PDF'}
          </button>
        ) : (
          <span className="text-[11px] text-gray-500">PDF no disponible</span>
        )}
      </div>
      {open && disponible && (
        <div className="border-t border-gray-800 p-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Cargando PDF…
            </div>
          ) : error ? (
            <p className="py-6 text-center text-sm text-red-400">{error}</p>
          ) : url ? (
            <iframe title={archivo || 'PDF'} src={url} className="w-full rounded-md bg-white" style={{ height: '70vh' }} />
          ) : null}
        </div>
      )}
    </div>
  );
};

const RunRowRecibo = ({ run, apiUrl, token, onRefresh, autoOpen, onAutoOpened }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creando, setCreando] = useState(false);
  // Asignaciones manuales de cliente hechas en el historial (cuit -> {clie_id_clie, ...})
  const [asignaciones, setAsignaciones] = useState({});
  const rowRef = useRef(null);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/recibos-cobro/runs/${run.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Error al cargar el detalle');
      setDetail(d);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) await fetchDetail();
  };

  // Apertura automática cuando se llega desde "Procesar PDF" (PDF ya existente).
  useEffect(() => {
    if (autoOpen && !open) {
      setOpen(true);
      if (!detail) fetchDetail();
      rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onAutoOpened?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  const onAsignar = (cuit, val) => setAsignaciones((p) => ({ ...p, [cuit]: val }));

  // Grupos que se pueden crear: pendientes/sin_cliente con cliente resuelto.
  const procesables = (detail?.grupos || []).filter(
    (g) => (g.estado_recibo === 'pendiente' || g.estado_recibo === 'sin_cliente')
      && (g.clie_id_clie || asignaciones[g.cuit]?.clie_id_clie)
      && (g.items || []).length
  );
  const sinClienteAun = (detail?.grupos || []).filter(
    (g) => (g.estado_recibo === 'pendiente' || g.estado_recibo === 'sin_cliente')
      && !(g.clie_id_clie || asignaciones[g.cuit]?.clie_id_clie)
  ).length;
  // Grupos marcados como duplicado: se pueden reintentar (el chequeo por
  // CUIT + monto + fecha decide de nuevo).
  const duplicados = (detail?.grupos || []).filter(
    (g) => g.estado_recibo === 'duplicado' && g.clie_id_clie && (g.items || []).length
  ).length;

  const crearPendientes = async (reintentarDuplicados = false) => {
    setCreando(true);
    try {
      const payloadAsign = Object.entries(asignaciones)
        .filter(([, v]) => v?.clie_id_clie)
        .map(([cuit, v]) => ({ cuit, clie_id_clie: v.clie_id_clie, cliente_nombre: v.cliente_nombre || null }));
      const res = await fetch(`${apiUrl}/api/admin/recibos-cobro/runs/${run.id}/crear-pendientes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ asignaciones: payloadAsign, reintentar_duplicados: !!reintentarDuplicados }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Error al crear pendientes');
      toast.success(d.mensaje || 'Pendientes procesados');
      setAsignaciones({});
      await fetchDetail();
      onRefresh?.();
    } catch (e) { toast.error(e.message); }
    finally { setCreando(false); }
  };

  return (
    <>
      <tr ref={rowRef} onClick={toggle} className={`border-b border-gray-800/60 hover:bg-gray-800/20 transition-colors cursor-pointer ${autoOpen ? 'bg-[#00a1df]/5' : ''}`}>
        <td className="px-4 py-3 text-gray-500">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{fmtFecha(run.started_at)}</td>
        <td className="px-4 py-3 text-sm font-semibold text-white max-w-[220px] truncate" title={run.archivo}>{run.archivo || '—'}</td>
        <td className="px-4 py-3">
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border bg-gray-700/40 text-gray-400 border-gray-600">
            {run.modo === 'cheques' ? 'Cheques' : 'Transferencias'}
          </span>
        </td>
        <td className="px-4 py-3"><RunStatusBadge status={run.status} /></td>
        <td className="px-4 py-3 text-xs text-gray-400 max-w-[280px] truncate">{run.summary || '—'}</td>
      </tr>
      {open && (
        <tr className="bg-[#0F1117]">
          <td colSpan={6} className="px-6 py-4">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando detalle...</div>
            ) : detail ? (
              <div className="space-y-4">
                <PdfViewer
                  apiUrl={apiUrl}
                  token={token}
                  runId={run.id}
                  archivo={detail.archivo}
                  disponible={!!detail.pdf_disponible}
                />
                <div className="flex flex-wrap gap-4 text-[11px] text-gray-500">
                  <span>Archivo: <span className="text-gray-300">{detail.archivo}</span></span>
                  <span>Ítems leídos: <span className="text-gray-300">{detail.total_items}</span>{detail.items_sin_cuit ? ` (${detail.items_sin_cuit} sin CUIT)` : ''}</span>
                  {detail.counts && (
                    <span>
                      Recibos: <span className="text-green-400">{detail.counts.creados} creados</span>
                      {detail.counts.pendientes ? <>, <span className="text-gray-300">{detail.counts.pendientes} pendientes</span></> : null}
                      {detail.counts.sin_cliente ? <>, <span className="text-amber-400">{detail.counts.sin_cliente} sin cliente</span></> : null}
                      {detail.counts.con_error ? <>, <span className="text-red-400">{detail.counts.con_error} con error</span></> : null}
                    </span>
                  )}
                  <span>Última actualización: <span className="text-gray-300">{fmtFecha(detail.updated_at)}</span></span>
                </div>

                {(procesables.length > 0 || sinClienteAun > 0 || duplicados > 0) && (
                  <div className="flex items-center justify-between gap-3 flex-wrap bg-blue-900/10 border border-blue-500/20 rounded-lg px-4 py-3">
                    <p className="text-xs text-blue-300/80">
                      {procesables.length > 0
                        ? `Hay ${procesables.length} recibo(s) listo(s) para crear.`
                        : sinClienteAun > 0
                          ? 'Asigná el cliente en los pendientes de abajo para poder crear los recibos.'
                          : `Hay ${duplicados} recibo(s) marcados como duplicados que se pueden reintentar.`}
                      {procesables.length > 0 && sinClienteAun > 0
                        ? ` (${sinClienteAun} todavía sin cliente)`
                        : ''}
                      {' '}Se verifican duplicados (cliente, monto y fecha) antes de crear.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {duplicados > 0 && (
                        <button
                          onClick={() => crearPendientes(true)}
                          disabled={creando}
                          title="Vuelve a verificar los duplicados: si el recibo existente tiene otra fecha de acreditación, se crea uno nuevo."
                          className="flex items-center gap-2 bg-amber-600/80 hover:bg-amber-600 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95 whitespace-nowrap"
                        >
                          {creando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                          {creando ? 'PROCESANDO…' : `REINTENTAR DUPLICADOS (${duplicados})`}
                        </button>
                      )}
                      <button
                        onClick={() => crearPendientes(false)}
                        disabled={creando || procesables.length === 0}
                        className="flex items-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95 whitespace-nowrap"
                      >
                        {creando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                        {creando ? 'CREANDO…' : `CREAR PENDIENTES${procesables.length ? ` (${procesables.length})` : ''}`}
                      </button>
                    </div>
                  </div>
                )}

                {(detail.grupos || []).length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-[10px] font-bold uppercase text-gray-500">Clientes y movimientos leídos</p>
                    {detail.grupos.map((g, i) => (
                      <GrupoLog
                        key={`${g.cuit}-${i}`}
                        grupo={g}
                        modo={detail.modo}
                        apiUrl={apiUrl}
                        token={token}
                        asignacion={asignaciones[g.cuit]}
                        onAsignar={onAsignar}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-600">No se detectaron movimientos válidos en este PDF.</p>
                )}

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-bold uppercase text-gray-500">Log de lectura del PDF</p>
                    <button onClick={() => { navigator.clipboard?.writeText(detail.log || ''); toast.success('Log copiado'); }}
                      className="text-[10px] font-bold text-gray-500 hover:text-gray-300">Copiar</button>
                  </div>
                  <pre className="text-[11px] text-gray-300 bg-black/40 border border-gray-800 rounded-lg p-3 whitespace-pre-wrap overflow-x-auto max-h-72 overflow-y-auto font-mono">
{detail.log || '(sin log)'}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600">Sin detalle.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const HistorialRecibos = ({ apiUrl, token, openRunId, onOpened }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fStatus, setFStatus] = useState('');

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fStatus) params.set('status', fStatus);
      params.set('limit', '100');
      const res = await fetch(`${apiUrl}/api/admin/recibos-cobro/runs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Error al cargar el historial');
      setRuns(d.runs || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [apiUrl, token, fStatus]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  return (
    <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl">
      <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#00a1df]" /> Historial de procesos
        </h3>
        <div className="flex items-center gap-2">
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
            className="bg-[#0F1117] border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-[#00a1df]">
            <option value="">Todos los estados</option>
            <option value="completo">Completo</option>
            <option value="parcial">Parcial</option>
            <option value="pendiente">Pendiente</option>
            <option value="error">Error</option>
          </select>
          <button onClick={fetchRuns}
            className="p-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-all" title="Recargar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex flex-col items-center gap-3 text-gray-600">
          <Loader2 className="w-7 h-7 animate-spin" /><p className="text-sm">Cargando historial...</p>
        </div>
      ) : runs.length === 0 ? (
        <div className="py-16 flex flex-col items-center gap-3 text-gray-600">
          <History className="w-7 h-7" /><p className="text-sm">No hay procesos registrados todavía</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0F1117] border-b border-gray-800 text-gray-500 uppercase text-[10px] font-black tracking-widest">
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3 text-left">Fecha y hora</th>
                <th className="px-4 py-3 text-left">Archivo</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Resumen</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <RunRowRecibo
                  key={run.id}
                  run={run}
                  apiUrl={apiUrl}
                  token={token}
                  onRefresh={fetchRuns}
                  autoOpen={openRunId === run.id}
                  onAutoOpened={onOpened}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RecibosCobroPage = () => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [vista, setVista] = useState('procesar');    // 'procesar' | 'historial'
  const [abrirRunId, setAbrirRunId] = useState(null); // run a abrir al entrar al historial

  // Documentos de banco pendientes (subidos por load-docs).
  const [pendientes, setPendientes] = useState([]);
  const [cargandoPend, setCargandoPend] = useState(false);
  const [procesandoId, setProcesandoId] = useState(null); // load_doc_id que se está analizando
  // Formulario de imputación manual (para documentos ilegibles / requiere_revision).
  const [manual, setManual] = useState({ open: false, doc: null, modo: 'cheques', items: [] });

  const [analizando, setAnalizando] = useState(false);
  const [modo, setModo] = useState(null);            // 'cheques' | 'transferencias'
  const [grupos, setGrupos] = useState([]);          // preview agrupado por cliente
  const [error, setError] = useState(null);
  const [runId, setRunId] = useState(null);          // id del proceso en el historial

  // clie_id_clie elegido por grupo (cuit -> id)
  const [seleccion, setSeleccion] = useState({});
  // razón social del cliente elegido por grupo (cuit -> nombre), para mostrar/guardar
  const [clienteNombres, setClienteNombres] = useState({});
  // vendedor del cliente elegido por grupo (cuit -> vendedor), solo para mostrar
  const [clienteVendedores, setClienteVendedores] = useState({});
  // estado por grupo (cuit -> 'idle'|'loading'|'ok'|'error')
  const [estados, setEstados] = useState({});
  // resultado por grupo (cuit -> { recibo_id, items_enviados, error })
  const [resultados, setResultados] = useState({});
  // expansión de detalle (cuit -> bool)
  const [expandidos, setExpandidos] = useState({});

  const resetPreview = () => {
    setModo(null);
    setGrupos([]);
    setRunId(null);
    setSeleccion({});
    setClienteNombres({});
    setClienteVendedores({});
    setEstados({});
    setResultados({});
    setExpandidos({});
  };

  // Lista de documentos de banco pendientes (cargados por load-docs).
  const fetchPendientes = useCallback(async () => {
    setCargandoPend(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/recibos-cobro/pendientes-cobro`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPendientes(data.documentos || []);
      }
    } catch {
      /* silencioso */
    } finally {
      setCargandoPend(false);
    }
  }, [API_URL, token]);

  useEffect(() => { if (vista === 'procesar') fetchPendientes(); }, [vista, fetchPendientes]);

  // Carga el preview (grupos por cliente) a partir de la respuesta de análisis.
  const cargarPreview = (data) => {
    setModo(data.modo);
    setGrupos(data.grupos || []);
    setRunId(data.run_id || null);
    const sel = {};
    const est = {};
    (data.grupos || []).forEach((g) => {
      if (g.clie_id_clie) sel[g.cuit] = g.clie_id_clie;
      est[g.cuit] = 'idle';
    });
    setSeleccion(sel);
    setEstados(est);
    if (!data.grupos || data.grupos.length === 0) {
      toast.info(data.mensaje || 'No se detectaron movimientos válidos.');
    } else {
      toast.success(
        `${data.modo === 'cheques' ? 'Cheques' : 'Transferencias'}: ${data.total_items} ítems en ${data.grupos.length} clientes.`
      );
    }
  };

  // Analiza un documento pendiente (cargado por load-docs) y arma el preview.
  const analizarDoc = async (doc) => {
    setProcesandoId(doc.id);
    setError(null);
    resetPreview();
    try {
      const res = await fetch(`${API_URL}/api/admin/recibos-cobro/analizar-doc`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ load_doc_id: doc.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      if (data.existing && data.run_id) {
        setAbrirRunId(data.run_id);
        setVista('historial');
        toast.info(data.mensaje || 'Este documento ya fue procesado. Te llevamos a su histórico.');
        return;
      }
      cargarPreview(data);
    } catch (err) {
      setError(err.message);
      toast.error('Error al analizar: ' + err.message);
    } finally {
      setProcesandoId(null);
    }
  };

  // ── Formulario de imputación manual (documentos ilegibles) ──────────────
  const filaVacia = (modoDoc) =>
    modoDoc === 'cheques'
      ? { numero: '', razon_social: '', cuit: '', fecha_pago: '', banco_emisor: '', importe: '' }
      : { tipo: '', razon_social: '', cuit: '', fecha: '', referencia: '', importe: '' };

  const abrirManual = (doc) => {
    const modoDoc = doc.modo || 'cheques';
    setManual({ open: true, doc, modo: modoDoc, items: [filaVacia(modoDoc)] });
  };
  const cerrarManual = () => setManual({ open: false, doc: null, modo: 'cheques', items: [] });
  const manualAgregarFila = () =>
    setManual((m) => ({ ...m, items: [...m.items, filaVacia(m.modo)] }));
  const manualQuitarFila = (idx) =>
    setManual((m) => ({ ...m, items: m.items.filter((_, i) => i !== idx) }));
  const manualSetCampo = (idx, campo, val) =>
    setManual((m) => ({
      ...m,
      items: m.items.map((it, i) => (i === idx ? { ...it, [campo]: val } : it)),
    }));

  const enviarManual = async () => {
    const { doc, modo: modoDoc, items } = manual;
    // Validación en el front (el backend vuelve a validar).
    for (const it of items) {
      const cuit = String(it.cuit || '').replace(/\D/g, '');
      const imp = parseFloat(String(it.importe).replace(',', '.'));
      if (cuit.length !== 11) { toast.error(`CUIT inválido: ${it.cuit || '(vacío)'} — deben ser 11 dígitos.`); return; }
      if (!imp || imp <= 0) { toast.error('Todos los importes deben ser mayores a 0.'); return; }
    }
    setAnalizando(true);
    try {
      const payload = {
        modo: modoDoc,
        load_doc_id: doc?.id || null,
        items: items.map((it) => ({
          numero: it.numero || '',
          razon_social: it.razon_social || '',
          cuit: String(it.cuit || '').replace(/\D/g, ''),
          fecha_pago: it.fecha_pago || null,
          banco_emisor: it.banco_emisor || '',
          tipo: it.tipo || '',
          fecha: it.fecha || null,
          referencia: it.referencia || '',
          importe: parseFloat(String(it.importe).replace(',', '.')),
        })),
      };
      const res = await fetch(`${API_URL}/api/admin/recibos-cobro/imputar-manual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      cerrarManual();
      cargarPreview(data);
    } catch (err) {
      toast.error('Error en la carga manual: ' + err.message);
    } finally {
      setAnalizando(false);
    }
  };

  const crearRecibo = async (grupo) => {
    const clieId = seleccion[grupo.cuit];
    if (!clieId) {
      toast.error(`Seleccioná un cliente para ${grupo.razon_social_pdf || grupo.cuit}.`);
      return;
    }
    setEstados((p) => ({ ...p, [grupo.cuit]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/recibos-cobro/confirmar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modo,
          run_id: runId,
          grupos: [{
            cuit: grupo.cuit,
            razon_social_pdf: grupo.razon_social_pdf,
            clie_id_clie: clieId,
            cliente_nombre: clienteNombres[grupo.cuit] || null,
            items: grupo.items,
          }],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);

      const r = (data.resultados || [])[0] || {};

      if (r.duplicado) {
        setEstados((p) => ({ ...p, [grupo.cuit]: 'duplicado' }));
        setResultados((p) => ({ ...p, [grupo.cuit]: { recibo_id: r.recibo_id, duplicado: true } }));
        toast.info(
          `Ya existía un recibo para ${grupo.razon_social_pdf || grupo.cuit}${r.recibo_id ? ` (#${r.recibo_id})` : ''}. No se creó de nuevo.`
        );
        return;
      }
      if (!r.ok) throw new Error(r.error || 'YiQi rechazó el recibo');

      setEstados((p) => ({ ...p, [grupo.cuit]: 'ok' }));
      setResultados((p) => ({
        ...p,
        [grupo.cuit]: { recibo_id: r.recibo_id, items_enviados: r.items_enviados },
      }));
      toast.success(`Recibo #${r.recibo_id} creado para ${grupo.razon_social_pdf || grupo.cuit}`);
    } catch (err) {
      setEstados((p) => ({ ...p, [grupo.cuit]: 'error' }));
      setResultados((p) => ({ ...p, [grupo.cuit]: { error: err.message } }));
      toast.error(`Error en ${grupo.razon_social_pdf || grupo.cuit}: ${err.message}`);
    }
  };

  const crearTodos = async () => {
    const listos = grupos.filter(
      (g) => estados[g.cuit] === 'idle' && seleccion[g.cuit]
    );
    if (listos.length === 0) {
      toast.info('No hay recibos listos para crear (revisá los clientes pendientes de selección).');
      return;
    }
    for (const g of listos) {
      // eslint-disable-next-line no-await-in-loop
      await crearRecibo(g);
    }
  };

  const toggleExpand = (cuit) =>
    setExpandidos((p) => ({ ...p, [cuit]: !p[cuit] }));

  const totalGeneral = grupos.reduce((acc, g) => acc + (g.total || 0), 0);
  const contador = grupos.reduce((acc, g) => {
    const e = estados[g.cuit] || 'idle';
    acc[e] = (acc[e] || 0) + 1;
    return acc;
  }, {});
  const esCheques = modo === 'cheques';

  return (
    <div className="text-gray-100">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#00a1df] to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
              <Receipt className="w-7 h-7 text-[#00a1df]" />
              Cheques y Transferencias
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Procesá los documentos de banco (Cheques recibidos o Movimientos de CC) que se cargan desde
              la herramienta de Carga de Documentos, y generá los recibos por cliente
            </p>
          </div>
        </div>

        {/* ── Pestañas ── */}
        <div className="flex gap-1 bg-[#1A1D27] border border-gray-800 rounded-xl p-1 w-fit">
          <button
            onClick={() => setVista('procesar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              vista === 'procesar' ? 'bg-[#00a1df] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <FileText className="w-4 h-4" /> Procesar PDF
          </button>
          <button
            onClick={() => setVista('historial')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              vista === 'historial' ? 'bg-[#00a1df] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <History className="w-4 h-4" /> Historial
          </button>
        </div>

        {vista === 'historial' && (
          <HistorialRecibos apiUrl={API_URL} token={token} openRunId={abrirRunId} onOpened={() => setAbrirRunId(null)} />
        )}

        {vista === 'procesar' && (
        <>
        {/* ── Documentos pendientes (cargados por load-docs) ── */}
        {!modo && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden">
          <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Inbox className="w-4 h-4 text-[#00a1df]" /> Documentos pendientes
            </h3>
            <button
              onClick={fetchPendientes}
              className="flex items-center gap-1.5 text-[11px] font-bold text-gray-400 hover:text-white transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${cargandoPend ? 'animate-spin' : ''}`} /> Actualizar
            </button>
          </div>

          {cargandoPend && pendientes.length === 0 ? (
            <div className="p-8 flex items-center justify-center text-gray-500 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </div>
          ) : pendientes.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              No hay documentos de banco pendientes. Se cargan desde la herramienta de
              <strong className="text-gray-400"> Carga de Documentos</strong> (cheques recibidos o movimientos de CC).
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {pendientes.map((doc) => {
                const esCh = doc.tipo_documento === 'cheques_recibidos';
                const revision = doc.requiere_revision;
                const procesando = procesandoId === doc.id;
                return (
                  <div key={doc.id} className="grid grid-cols-[1.6fr,1fr,auto] items-center gap-4 px-6 py-4 hover:bg-gray-700/20 transition-colors">
                    <div className="min-w-0">
                      <p className="font-bold text-white truncate flex items-center gap-2">
                        {esCh ? <Banknote className="w-4 h-4 text-[#00a1df] shrink-0" /> : <ArrowRightLeft className="w-4 h-4 text-[#00a1df] shrink-0" />}
                        {doc.archivo_nombre || '(sin nombre)'}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {esCh ? 'Cheques recibidos' : 'Movimientos de CC'} · {fmtFecha(doc.created_at)}
                        {doc.user_name ? ` · ${doc.user_name}` : ''}
                      </p>
                    </div>
                    <div>
                      {revision ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-md">
                          <FileWarning className="w-3.5 h-3.5" /> No se pudo leer — carga manual
                        </span>
                      ) : doc.origen_texto === 'ocr' || doc.origen_texto === 'ocr_llm' ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                          <FileText className="w-3.5 h-3.5" /> Leído por OCR
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                          <FileText className="w-3.5 h-3.5" /> Listo para procesar
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      {doc.drive_url && (
                        <a href={doc.drive_url} target="_blank" rel="noreferrer"
                          className="p-2 text-gray-500 hover:text-gray-200 rounded-lg hover:bg-gray-700/40 transition-colors" title="Ver en Drive">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      {revision ? (
                        <button
                          onClick={() => abrirManual(doc)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black bg-amber-600 hover:bg-amber-500 text-white transition-all active:scale-95"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Cargar a mano
                        </button>
                      ) : (
                        <button
                          onClick={() => analizarDoc(doc)}
                          disabled={procesando}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 disabled:text-gray-500 text-white transition-all active:scale-95"
                        >
                          {procesando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5 fill-current" />}
                          {procesando ? 'Analizando…' : 'Procesar'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-5 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
            <p className="text-red-300/90 text-sm">{error}</p>
          </div>
        )}

        {/* ── Preview ── */}
        {modo && grupos.length > 0 && (
          <>
            {/* Stats + acciones */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                <StatCard
                  label="Tipo"
                  value={esCheques ? 'Cheques' : 'Transferencias'}
                  icon={esCheques ? <Banknote className="w-5 h-5" /> : <ArrowRightLeft className="w-5 h-5" />}
                  color="text-[#00a1df]"
                  bg="bg-[#00a1df]/10"
                />
                <StatCard
                  label="Clientes"
                  value={grupos.length}
                  icon={<Building2 className="w-5 h-5" />}
                  color="text-purple-400"
                  bg="bg-purple-500/10"
                />
                <StatCard
                  label="Total"
                  value={fmtMoney(totalGeneral)}
                  icon={<Hash className="w-5 h-5" />}
                  color="text-green-400"
                  bg="bg-green-500/10"
                />
                <StatCard
                  label="Recibos creados"
                  value={contador.ok || 0}
                  icon={<CheckCircle2 className="w-5 h-5" />}
                  color="text-emerald-400"
                  bg="bg-emerald-500/10"
                />
              </div>
              <button
                onClick={crearTodos}
                className="flex items-center justify-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] text-white px-5 py-2.5 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95 whitespace-nowrap"
              >
                <Play className="w-4 h-4 fill-current" />
                CREAR TODOS
              </button>
            </div>

            {/* Tabla de clientes */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
              <div className="p-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#00a1df]" />
                  Clientes detectados
                </h3>
              </div>

              <div className="divide-y divide-gray-700/50">
                {grupos.map((g) => {
                  const estado = estados[g.cuit] || 'idle';
                  const resultado = resultados[g.cuit];
                  const isExpanded = expandidos[g.cuit];
                  const candidatos = g.candidatos || [];
                  const noEncontrado = g.estado_cliente === 'no_encontrado';
                  const requiereSeleccion = candidatos.length > 1;

                  return (
                    <div key={g.cuit}>
                      <div className="grid grid-cols-[2fr,1.4fr,1fr,auto,auto] items-center px-6 py-4 hover:bg-gray-700/20 transition-colors gap-4">

                        {/* Cliente */}
                        <div className="min-w-0">
                          <p className="font-bold text-white truncate">
                            {g.razon_social_pdf || '(sin nombre)'}
                          </p>
                          <p className="text-[10px] text-gray-500 font-mono">
                            CUIT {g.cuit} · {g.cantidad_items} {esCheques ? 'cheque(s)' : 'transf.'} · {fmtMoney(g.total)}
                          </p>
                        </div>

                        {/* Selección de cliente YiQi */}
                        <div className="min-w-0">
                          {noEncontrado ? (
                            <div className="space-y-1">
                              {!seleccion[g.cuit] && (
                                <span className="flex items-center gap-1.5 text-[10px] text-amber-400">
                                  <UserX className="w-3 h-3" /> Sin cliente en YiQi — elegí uno
                                </span>
                              )}
                              <BuscadorCliente
                                apiUrl={API_URL}
                                token={token}
                                value={seleccion[g.cuit]}
                                nombre={clienteNombres[g.cuit]}
                                vendedor={clienteVendedores[g.cuit]}
                                disabled={estado === 'ok' || estado === 'loading'}
                                onSelect={(c) => {
                                  setSeleccion((p) => ({ ...p, [g.cuit]: c.clie_id_clie }));
                                  setClienteNombres((p) => ({ ...p, [g.cuit]: c.razon_social }));
                                  setClienteVendedores((p) => ({ ...p, [g.cuit]: c.vendedor || '' }));
                                }}
                              />
                            </div>
                          ) : requiereSeleccion ? (
                            <select
                              value={seleccion[g.cuit] || ''}
                              onChange={(e) => {
                                const id = Number(e.target.value);
                                setSeleccion((p) => ({ ...p, [g.cuit]: id }));
                                const c = candidatos.find((x) => x.clie_id_clie === id);
                                setClienteNombres((p) => ({ ...p, [g.cuit]: c?.razon_social || null }));
                              }}
                              disabled={estado === 'ok' || estado === 'loading'}
                              className="w-full bg-gray-900 border border-gray-600 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#00a1df] focus:outline-none disabled:opacity-50"
                            >
                              <option value="">— Elegir cliente —</option>
                              {candidatos.map((c) => (
                                <option key={c.clie_id_clie} value={c.clie_id_clie}>
                                  {c.razon_social} (ID {c.clie_id_clie})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="flex items-center gap-1.5 text-xs text-gray-400">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-500/70" />
                              {candidatos[0]?.razon_social || '—'}
                              <span className="text-gray-600">· ID {candidatos[0]?.clie_id_clie}</span>
                            </span>
                          )}
                        </div>

                        {/* Estado */}
                        <div>
                          {estado === 'idle' && <span className="text-xs text-gray-500 italic">— pendiente —</span>}
                          {estado === 'loading' && (
                            <span className="flex items-center gap-1.5 text-xs text-[#00a1df]">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Creando...
                            </span>
                          )}
                          {estado === 'ok' && resultado && (
                            <div>
                              <span className="flex items-center gap-1 text-xs text-green-400 font-bold">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Recibo #{resultado.recibo_id}
                              </span>
                              <span className="text-[10px] text-gray-500">{resultado.items_enviados} ítems</span>
                            </div>
                          )}
                          {estado === 'duplicado' && (
                            <div>
                              <span className="flex items-center gap-1 text-xs text-amber-400 font-bold">
                                <AlertCircle className="w-3.5 h-3.5" /> Ya existía{resultado?.recibo_id ? ` #${resultado.recibo_id}` : ''}
                              </span>
                              <span className="text-[10px] text-gray-500">No se creó de nuevo</span>
                            </div>
                          )}
                          {estado === 'error' && resultado && (
                            <div>
                              <span className="flex items-center gap-1 text-xs text-red-400 font-bold">
                                <XCircle className="w-3.5 h-3.5" /> Error
                              </span>
                              <span className="text-[10px] text-red-400/70 truncate block max-w-[140px]">
                                {resultado.error}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Botón crear */}
                        <button
                          onClick={() => crearRecibo(g)}
                          disabled={estado === 'loading' || estado === 'ok' || estado === 'duplicado' || !seleccion[g.cuit]}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all active:scale-95 ${
                            estado === 'ok'
                              ? 'bg-green-900/30 text-green-400 border border-green-500/20 cursor-default'
                              : estado === 'duplicado'
                              ? 'bg-amber-900/30 text-amber-400 border border-amber-500/20 cursor-default'
                              : estado === 'loading'
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : !seleccion[g.cuit]
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : estado === 'error'
                              ? 'bg-red-600 hover:bg-red-500 text-white'
                              : 'bg-[#00a1df] hover:bg-[#0090c7] text-white shadow-lg shadow-[#00a1df]/20'
                          }`}
                        >
                          {estado === 'loading' ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : estado === 'ok' ? (
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          ) : estado === 'duplicado' ? (
                            <AlertCircle className="w-3.5 h-3.5" />
                          ) : (
                            <Receipt className="w-3.5 h-3.5" />
                          )}
                          {estado === 'ok' ? 'Creado' : estado === 'duplicado' ? 'Ya existía' : estado === 'error' ? 'Reintentar' : 'Crear recibo'}
                        </button>

                        {/* Toggle detalle */}
                        <button
                          onClick={() => toggleExpand(g.cuit)}
                          className="p-2 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-gray-700/40 transition-colors"
                          title="Ver detalle"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>

                      {/* Detalle expandible */}
                      {isExpanded && (
                        <div className="bg-gray-900/60 border-t border-gray-700/50 px-6 py-4">
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500 uppercase tracking-wider border-b border-gray-700/50">
                                  {esCheques ? (
                                    <>
                                      <th className="text-left py-2 pr-4 font-bold">Nº Cheque</th>
                                      <th className="text-left py-2 pr-4 font-bold">Banco emisor</th>
                                      <th className="text-left py-2 pr-4 font-bold">Fecha pago</th>
                                      <th className="text-right py-2 font-bold">Importe</th>
                                    </>
                                  ) : (
                                    <>
                                      <th className="text-left py-2 pr-4 font-bold">Tipo</th>
                                      <th className="text-left py-2 pr-4 font-bold">Referencia</th>
                                      <th className="text-left py-2 pr-4 font-bold">Fecha</th>
                                      <th className="text-right py-2 font-bold">Importe</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-800">
                                {g.items.map((it, idx) => (
                                  <tr key={idx} className="hover:bg-gray-800/40 transition-colors">
                                    {esCheques ? (
                                      <>
                                        <td className="py-2 pr-4 font-mono text-cyan-400">{it.numero || '—'}</td>
                                        <td className="py-2 pr-4 text-gray-300 max-w-xs truncate" title={it.banco_emisor}>{it.banco_emisor || '—'}</td>
                                        <td className="py-2 pr-4 text-gray-400">{it.fecha_pago || '—'}</td>
                                        <td className="py-2 text-right font-bold text-white">{fmtMoney(it.importe)}</td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="py-2 pr-4 text-gray-300">{it.tipo || '—'}</td>
                                        <td className="py-2 pr-4 text-gray-400 max-w-xs truncate" title={it.referencia}>{it.referencia || '—'}</td>
                                        <td className="py-2 pr-4 text-gray-400">{it.fecha || '—'}</td>
                                        <td className="py-2 text-right font-bold text-white">{fmtMoney(it.importe)}</td>
                                      </>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Info footer */}
            <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg p-4 flex gap-3 text-sm text-blue-300/80">
              <Hash className="w-5 h-5 shrink-0 mt-0.5 text-blue-400" />
              <p>
                Cada recibo se crea por cliente en YiQi (Schema <strong className="text-blue-300">1383</strong>, Punto de venta <strong className="text-blue-300">7</strong>, fecha de cobro = hoy).
                {esCheques
                  ? ' Los cheques se cargan con banco "ELECTRONICOS" (007-160) y banco depositado "Galicia CC Pesos 2943/6".'
                  : ' Las transferencias se cargan con cuenta destino "Galicia CC Pesos 2943/6". Se incluyen solo créditos de clientes (se descartan impuestos, débitos y echeq).'}
              </p>
            </div>
          </>
        )}

        {/* Reset */}
        {modo && (
          <div className="flex justify-end">
            <button
              onClick={() => { resetPreview(); setError(null); fetchPendientes(); }}
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Volver a pendientes
            </button>
          </div>
        )}
        </>
        )}

      </div>

      {/* ── Modal de imputación manual ── */}
      {manual.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={cerrarManual}>
          <div
            className="bg-[#1A1D27] border border-gray-700 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-amber-400" /> Carga manual —{' '}
                  {manual.modo === 'cheques' ? 'Cheques recibidos' : 'Movimientos de CC'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{manual.doc?.archivo_nombre}</p>
              </div>
              <button onClick={cerrarManual} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              <div className="space-y-3">
                {manual.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    {manual.modo === 'cheques' ? (
                      <>
                        <input className="col-span-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" placeholder="Nº cheque" value={it.numero} onChange={(e) => manualSetCampo(idx, 'numero', e.target.value)} />
                        <input className="col-span-3 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" placeholder="Razón social" value={it.razon_social} onChange={(e) => manualSetCampo(idx, 'razon_social', e.target.value)} />
                        <input className="col-span-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" placeholder="CUIT (11 díg.)" value={it.cuit} onChange={(e) => manualSetCampo(idx, 'cuit', e.target.value)} />
                        <input type="date" className="col-span-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" value={it.fecha_pago} onChange={(e) => manualSetCampo(idx, 'fecha_pago', e.target.value)} />
                        <input className="col-span-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white text-right" placeholder="Importe" value={it.importe} onChange={(e) => manualSetCampo(idx, 'importe', e.target.value)} />
                        <button onClick={() => manualQuitarFila(idx)} className="col-span-1 text-gray-500 hover:text-red-400 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                        <input className="col-span-11 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-gray-400" placeholder="Banco emisor (opcional)" value={it.banco_emisor} onChange={(e) => manualSetCampo(idx, 'banco_emisor', e.target.value)} />
                      </>
                    ) : (
                      <>
                        <input className="col-span-3 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" placeholder="Tipo (ej. TRANSFERENCIA…)" value={it.tipo} onChange={(e) => manualSetCampo(idx, 'tipo', e.target.value)} />
                        <input className="col-span-3 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" placeholder="Razón social" value={it.razon_social} onChange={(e) => manualSetCampo(idx, 'razon_social', e.target.value)} />
                        <input className="col-span-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" placeholder="CUIT (11 díg.)" value={it.cuit} onChange={(e) => manualSetCampo(idx, 'cuit', e.target.value)} />
                        <input type="date" className="col-span-2 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white" value={it.fecha} onChange={(e) => manualSetCampo(idx, 'fecha', e.target.value)} />
                        <input className="col-span-1 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-white text-right" placeholder="Importe" value={it.importe} onChange={(e) => manualSetCampo(idx, 'importe', e.target.value)} />
                        <button onClick={() => manualQuitarFila(idx)} className="col-span-1 text-gray-500 hover:text-red-400 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                        <input className="col-span-11 bg-gray-900 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-gray-400" placeholder="Referencia (opcional)" value={it.referencia} onChange={(e) => manualSetCampo(idx, 'referencia', e.target.value)} />
                      </>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={manualAgregarFila} className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-[#00a1df] hover:text-[#4bb8e8]">
                <Plus className="w-4 h-4" /> Agregar movimiento
              </button>
            </div>

            <div className="p-5 border-t border-gray-700 flex items-center justify-between">
              <p className="text-[11px] text-gray-500">CUIT de 11 dígitos e importe {'>'} 0 en cada fila.</p>
              <div className="flex gap-2">
                <button onClick={cerrarManual} className="px-4 py-2 rounded-lg text-xs font-bold text-gray-300 hover:bg-gray-800">Cancelar</button>
                <button onClick={enviarManual} disabled={analizando} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-black bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 text-white">
                  {analizando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Cargar y previsualizar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon, color, bg }) => (
  <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 backdrop-blur-sm">
    <div className="flex justify-between items-start">
      <div className="min-w-0">
        <p className="text-gray-400 text-sm">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${color} truncate`}>{value}</p>
      </div>
      <div className={`${bg} p-2 rounded-lg ${color} shrink-0`}>{icon}</div>
    </div>
  </div>
);

export default RecibosCobroPage;
